import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { Award, ClipboardList, Download, FileText } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidate,
  useCandidatesList,
  useEffectiveUser,
  useExamAttemptsForCandidate,
  useExpedientes,
} from "@/store/hooks";

export default function CandidateHistory() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const candidateId =
    effective && effective.roles.includes("alumno") ? effective.id : candList[0]?.id;
  const me = useCandidate(candidateId);

  const attempts = useExamAttemptsForCandidate(candidateId);
  const expedientes = useExpedientes();
  const m = useDemoStore((s) => s.mutations);

  const myExpedientes = useMemo(
    () => Object.values(expedientes).filter((e) => e.candidatoId === candidateId),
    [expedientes, candidateId]
  );

  const sorted = useMemo(
    () => [...attempts].sort((a, b) => (a.fecha < b.fecha ? 1 : -1)),
    [attempts]
  );

  const handleDownload = (expId: string, folio: string | null) => {
    const content = `CERTIFICADO DE COMPETENCIA\n\nExpediente: ${expId}\nFolio: ${folio ?? "—"}\nCandidato: ${me?.nombre ?? "—"}\nFecha: ${new Date().toISOString().slice(0, 10)}\n\n(Documento simulado para fines de la demostración.)`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${folio ?? expId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!me) {
    return (
      <AppLayout breadcrumb={["Mi historial"]}>
        <Card><div style={{ padding: 24, color: "var(--cc-gray-600)" }}>No hay candidato activo.</div></Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb={["Mi historial"]}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Mi historial</h1>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>Intentos de evaluación y certificados emitidos.</div>
      </div>

      <Card>
        <SectionTitle>Intentos de evaluación</SectionTitle>
        {sorted.length === 0 ? (
          <div style={{ padding: 14, color: "var(--cc-gray-600)", fontSize: 13 }}>
            Aún no ha realizado ningún diagnóstico ni examen teórico.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--cc-gray-500)", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>Fecha</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>Estándar</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>Tipo</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>Puntaje</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => (
                  <tr key={a.id} data-testid={`row-attempt-${a.id}`}>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>{a.fecha}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>{a.estandar}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>
                      <Badge tone={a.kind === "teorico" ? "teal" : "amber"}>
                        {a.kind === "teorico" ? "Teórico" : "Diagnóstico"}
                      </Badge>
                    </td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)", fontWeight: 600 }}>{a.score} / 100</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--cc-gray-100)" }}>
                      <Badge tone={a.aprobado ? "green" : "amber"}>{a.aprobado ? "Aprobado" : "No aprobado"}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card style={{ marginTop: 14 }}>
        <SectionTitle>Mis certificados</SectionTitle>
        {myExpedientes.filter((e) => e.certificado.emitido || m.certificatesIssued.includes(e.id)).length === 0 ? (
          <div style={{ padding: 14, color: "var(--cc-gray-600)", fontSize: 13 }}>
            Aún no tiene certificados emitidos.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {myExpedientes
              .filter((e) => e.certificado.emitido || m.certificatesIssued.includes(e.id))
              .map((e) => (
                <div key={e.id} data-testid={`cert-${e.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--cc-green-bg)", color: "var(--cc-green)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Award size={17} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>Certificado {e.estandar}</div>
                    <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                      Folio {e.certificado.folio ?? "—"} · emitido el {e.certificado.fechaEmision ?? "—"}
                    </div>
                  </div>
                  <Link href={`/dictaminador/certificado/${e.id}`}>
                    <PrimaryButton variant="ghost"><FileText size={13} /> Ver</PrimaryButton>
                  </Link>
                  <PrimaryButton onClick={() => handleDownload(e.id, e.certificado.folio)} testId={`button-download-${e.id}`}>
                    <Download size={13} /> Descargar
                  </PrimaryButton>
                </div>
              ))}
          </div>
        )}
      </Card>

      <div style={{ marginTop: 14 }}>
        <Link href="/alumno"><PrimaryButton variant="ghost"><ClipboardList size={13} /> ← Volver al tablero</PrimaryButton></Link>
      </div>
    </AppLayout>
  );
}
